enum MessageCategory {
  MESSAGE = 0,
  REPORT = 1,
  REACTION = 2,
  SERVICE = 3,
  DELETED = 4,
  COMMENT = 5,
}

enum MessageType {
  NOTE = "NOTE",
  GROUP = "GROUP",
  P2P = "P2P",
}

type GetMessagesPayload = {
  chat_id: string;
  /**
   * UUID7 message ID cursor for backward pagination (not a timestamp).
   * Messages with ID < sent_before will be returned,
   * using UUID7's lexicographic ordering for chronological pagination.
   * Note: Despite the legacy "sent_before" name, this is a message ID (UUID7), not a timestamp.
   */
  sent_before?: string;
  /**
   * UUID7 message ID cursor for forward pagination (not a timestamp).
   * Messages with ID > sent_after will be returned,
   * using UUID7's lexicographic ordering for chronological pagination.
   * Note: Despite the legacy "sent_after" name, this is a message ID (UUID7), not a timestamp.
   */
  sent_after?: string;
  take?: number;
  category: MessageCategory;
  category1?: MessageCategory;
  category2?: MessageCategory;
  category3?: MessageCategory;
  parent_id?: string;
  thread_id?: string;
  sender?: string;
  has_links?: boolean;
  has_attachment?: boolean;
};

type GetMessagePayload = {
  message_id: string;
  chat_id: string;
  category: MessageCategory;
};

type DeleteMessagePayload = {
  message_id: string;
  chat_id: string;
  message_type: MessageType;
  category: MessageCategory;
};

type EncryptedLastMessage = {
  client_id?: string; // client-generated ID for optimistic updates,
  id: string;
  chat_id: string;
  sender: string;
  content: string;
  is_edited: boolean;
  category: MessageCategory;
  parent_id: string;
  parent_category: MessageCategory;
  attachment_meta: string;
  sender_avatar: string | null;
  // Read-time anonymisation marker (account deletion). ABSENT for live
  // senders. The legacy Rust API never sends it; typed for forward compat.
  sender_deleted?: boolean;
  tags: string[];
  comment_count: number | null;
  thread_id: string | null;
  parent_comment_count: number | null;
  key_epoch?: number; // CSK rotation epoch (0 = original key, absent = epoch 0)
};

type UserReaction = {
  content: string;
  message_id: string;
};

type EncryptedMessage = {
  client_id?: string; // client-generated ID for optimistic updates,
  id: string;
  chat_id: string;
  sender: string;
  content: string;
  is_edited: boolean;
  category: MessageCategory;
  parent_id: string;
  parent_category: MessageCategory;
  attachment_meta: string;
  sender_avatar: string | null;
  // Read-time anonymisation marker (account deletion). ABSENT for live
  // senders. The legacy Rust API never sends it; typed for forward compat.
  sender_deleted?: boolean;
  reactions: Record<string, number>;
  user_reactions: UserReaction[];
  tags: string[];
  comment_count: number | null;
  thread_id: string | null;
  parent_comment_count: number | null;
  key_epoch?: number; // CSK rotation epoch (0 = original key, absent = epoch 0)
};

/**
 * Epoch payloads returned inline with paginated message responses.
 * Maps epoch number → user's eCSK for that epoch.
 */
type EpochPayloads = Record<number, string>;

export type {
  EncryptedMessage,
  EncryptedLastMessage,
  EpochPayloads,
  GetMessagesPayload,
  GetMessagePayload,
  DeleteMessagePayload,
  UserReaction,
};

export { MessageCategory, MessageType };
